/**
 * Microsoft Teams OAuth2 Credential Form
 *
 * Comprehensive form for Microsoft Teams OAuth2 credentials:
 * - Azure AD Configuration (TenantId, ClientId, ClientSecret)
 * - OAuth flow initiation
 * - Connection status display
 * - Token expiry tracking
 */

import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, XCircle, Loader2, Info, ExternalLink, RefreshCw } from 'lucide-react';
import { credentialsApi } from '@/lib/api';

export interface TeamsOAuthFormData {
  tenantId: string;
  clientId: string;
  clientSecret: string;
  scopes: string;

  // System-managed (populated after OAuth)
  accessToken?: string;
  refreshToken?: string;
  tokenExpiresAt?: string;
  tokenType?: string;
  userEmail?: string;
  userName?: string;
  userId?: string;
}

interface MicrosoftTeamsOAuthFormProps {
  formData: TeamsOAuthFormData;
  onChange: (data: Partial<TeamsOAuthFormData>) => void;
  isEditing?: boolean;
  credentialId?: string;
  testResult?: { success: boolean; message: string; details?: any } | null;
  testing?: boolean;
  onTest?: () => void;
  /** Which section to render - 'config', 'status', or 'all' (default, shows internal tabs) */
  section?: 'config' | 'status' | 'all';
}

export function MicrosoftTeamsOAuthForm({
  formData,
  onChange,
  isEditing = false,
  credentialId,
  testResult,
  testing = false,
  onTest,
  section = 'all',
}: MicrosoftTeamsOAuthFormProps) {
  const [activeTab, setActiveTab] = useState('config');
  const [oauthStatus, setOauthStatus] = useState<any>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const handleChange = (field: keyof TeamsOAuthFormData, value: any) => {
    onChange({ [field]: value });
  };

  // Load OAuth status if editing existing credential
  useEffect(() => {
    if (isEditing && credentialId) {
      loadOAuthStatus();
    }
  }, [isEditing, credentialId]);

  const loadOAuthStatus = async () => {
    if (!credentialId) return;

    setLoadingStatus(true);
    try {
      const response = await credentialsApi.getOAuthStatus(credentialId);
      setOauthStatus(response.data);
    } catch (error) {
      console.error('Failed to load OAuth status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleConnectToMicrosoft = async () => {
    if (!credentialId) {
      alert('Please save the credential first before connecting to Microsoft');
      return;
    }

    try {
      const response = await credentialsApi.getAuthorizationUrl(credentialId);

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        response.data.authorizationUrl,
        'Microsoft OAuth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes`
      );

      if (!popup) {
        alert('Popup blocked! Please allow popups for this site and try again.');
        return;
      }

      // Listen for postMessage from the OAuth callback popup for immediate feedback
      const messageHandler = (event: MessageEvent) => {
        if (event.data?.type === 'oauth-callback') {
          window.removeEventListener('message', messageHandler);
          loadOAuthStatus();
          setActiveTab('status');
        }
      };
      window.addEventListener('message', messageHandler);

      // Fallback: poll to detect when popup closes
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollTimer);
          window.removeEventListener('message', messageHandler);
          loadOAuthStatus();
          setActiveTab('status');
        }
      }, 500);

    } catch (error: any) {
      alert(`Failed to get authorization URL: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleRefreshToken = async () => {
    if (!credentialId) return;

    setRefreshing(true);
    try {
      await credentialsApi.refreshOAuthToken(credentialId, 'teams');
      await loadOAuthStatus();
      alert('Token refreshed successfully');
    } catch (error: any) {
      alert(`Failed to refresh token: ${error.response?.data?.error || error.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  const isAuthorized = oauthStatus?.authorized || !!formData.accessToken;
  const isExpired = oauthStatus?.isExpired || false;
  const tokenExpiresAt = oauthStatus?.expiresAt || formData.tokenExpiresAt;

  // Config section content
  const configContent = (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="tenantId" className="text-sm font-medium">
          Tenant ID <span className="text-red-500">*</span>
        </Label>
        <Input
          id="tenantId"
          placeholder="00000000-0000-0000-0000-000000000000"
          value={formData.tenantId}
          onChange={(e) => handleChange('tenantId', e.target.value)}
          className="font-mono text-sm"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="clientId" className="text-sm font-medium">
          Application (Client) ID <span className="text-red-500">*</span>
        </Label>
        <Input
          id="clientId"
          placeholder="00000000-0000-0000-0000-000000000000"
          value={formData.clientId}
          onChange={(e) => handleChange('clientId', e.target.value)}
          className="font-mono text-sm"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="clientSecret" className="text-sm font-medium">
          Client Secret <span className="text-red-500">*</span>
        </Label>
        <Input
          id="clientSecret"
          type="password"
          placeholder="Enter client secret"
          value={formData.clientSecret}
          onChange={(e) => handleChange('clientSecret', e.target.value)}
          className="font-mono text-sm"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="scopes" className="text-sm font-medium">
          Scopes
        </Label>
        <Input
          id="scopes"
          placeholder="User.Read,Chat.ReadWrite,ChannelMessage.Send"
          value={formData.scopes || 'offline_access,User.Read,Chat.ReadWrite,ChannelMessage.Send,ChannelMessage.Read.All,Team.ReadBasic.All,Channel.ReadBasic.All,Group.ReadWrite.All,Files.ReadWrite.All,Calendars.ReadWrite'}
          onChange={(e) => handleChange('scopes', e.target.value)}
          className="font-mono text-sm"
        />
      </div>
    </div>
  );

  // Status section content
  const statusContent = (
    <div className="space-y-4">
      {loadingStatus ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <>
          <div className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Authorization Status</span>
              {isAuthorized ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="text-sm font-medium">Connected</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-gray-500">
                  <XCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">Not Connected</span>
                </div>
              )}
            </div>

            {isAuthorized && (
              <>
                {oauthStatus?.userEmail && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Connected Account</span>
                    <span className="font-mono">{oauthStatus.userEmail}</span>
                  </div>
                )}

                {oauthStatus?.userName && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Display Name</span>
                    <span>{oauthStatus.userName}</span>
                  </div>
                )}

                {tokenExpiresAt && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Token Expires</span>
                    <span className={isExpired ? 'text-red-600 font-medium' : ''}>
                      {new Date(tokenExpiresAt).toLocaleString()}
                      {isExpired && ' (Expired)'}
                    </span>
                  </div>
                )}

                {oauthStatus?.hasRefreshToken && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Refresh Token</span>
                    <span className="text-green-600">Available</span>
                  </div>
                )}
              </>
            )}
          </div>

          {!isAuthorized && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Save the credential first, then click "Connect to Microsoft" to authorize access.
              </AlertDescription>
            </Alert>
          )}

          {isExpired && (
            <Alert className="bg-amber-50 border-amber-200">
              <Info className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-900">
                Your access token has expired. Click "Refresh Token" to renew it.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            <Button
              onClick={handleConnectToMicrosoft}
              disabled={!credentialId || !formData.tenantId || !formData.clientId}
              className="flex-1"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {isAuthorized ? 'Reconnect to Microsoft' : 'Connect to Microsoft'}
            </Button>

            {isAuthorized && (
              <Button
                onClick={handleRefreshToken}
                disabled={refreshing || !oauthStatus?.hasRefreshToken}
                variant="outline"
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );

  // If section is specified, render just that section
  if (section === 'config') {
    return configContent;
  }

  if (section === 'status') {
    return statusContent;
  }

  // Default: render with internal tabs (backwards compatibility)
  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="config">Azure AD Configuration</TabsTrigger>
          <TabsTrigger value="status">Connection Status</TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="mt-4">
          {configContent}
        </TabsContent>

        <TabsContent value="status" className="mt-4">
          {statusContent}
        </TabsContent>
      </Tabs>

      {/* Test Connection Result */}
      {testResult && (
        <Alert className={testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
          {testResult.success ? (
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          ) : (
            <XCircle className="h-4 w-4 text-red-600" />
          )}
          <AlertDescription className={testResult.success ? 'text-green-900' : 'text-red-900'}>
            <strong>{testResult.success ? 'Success' : 'Failed'}:</strong> {testResult.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Test Connection Button */}
      {onTest && (
        <Button
          onClick={onTest}
          disabled={testing || !formData.tenantId || !formData.clientId}
          variant="outline"
          className="w-full"
        >
          {testing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Testing Connection...
            </>
          ) : (
            'Test Connection'
          )}
        </Button>
      )}
    </div>
  );
}
